import React, { PropsWithChildren, ReactNode } from 'react';
import './index.scss';
import _ from 'lodash';
import { DnDKitDragDrop } from './DnDKitDragDrop';

// if you want everything to be draggable and don't want to construct the interface
export const makeDefaultDragDropElementWrapper = (els: ReactNode[]): DragDropElementWrapper[] => {
  return els.map((el) => {
    return {
      dragAndDroppable: true,
      element: el,
    };
  });
};

// if you don't want everything to be draggable
export const makeCustomDragDropElementWrapper = (
  els: ReactNode[],
  isDraggableFunc: (el: ReactNode) => boolean,
): DragDropElementWrapper[] =>
  els.map((el) => ({
    dragAndDroppable: isDraggableFunc(el),
    element: el,
  }));

export interface DragDropElementWrapper<T = unknown> {
  className?: string;
  id?: string;
  dragAndDroppable: boolean;
  droppable?: boolean;
  element: ReactNode; // Main content or title element for CONDITIONS type
  trailingElement?: ReactNode; // Trailing content for lists
  additionalItemProps?: Record<string, unknown>; // Additional props to pass to the container
  mainElement?: ReactNode; // Main content for CONDITIONS type
  value?: T; // Data associated with the item
  iconName?: string; // Icon name for drag handle
}

export enum DragDropContainerType {
  LIST = 'LIST',
  TABLE = 'TABLE',
  FORGE_LIST = 'FORGE_LIST',
  CONDITIONS = 'CONDITIONS',
  FORGE_CARD = 'FORGE_CARD',
}

export interface DragDropContainerProps<T> {
  className?: string;
  type: DragDropContainerType;
  iconName?: string; // Icon name for drag handle
  items: T[];
  childElements: DragDropElementWrapper[];
  tableHeadContent?: ReactNode; // required if type is TABLE
  onDrop: (newItems: T[]) => void;
  dense?: boolean;
  useHoverColor?: boolean; // defaults to true
  sourceComponent?: string | undefined;
  usePortal?: boolean; // For dnd-kit: whether to render DragOverlay in a portal
}

// Enhanced wrapper component that handles dnd-kit implementation
export const DragDropContainer = <T = unknown,>(props: PropsWithChildren<DragDropContainerProps<T>>) => {
  // Convert props to dnd-kit format
  const { items, onDrop, childElements, type, className, dense, tableHeadContent } = props;

  // Handle conversion for dnd-kit
  const handleDndKitReorder = (newItems: DragDropElementWrapper<T>[]) => {
    // Extract the original data from the dnd-kit items
    const reorderedData = newItems.map((item) => item.value || item) as T[];
    onDrop(reorderedData);
  };

  // Use childElements directly since they're already in DragDropElementWrapper format
  let dndKitItems: DragDropElementWrapper<T>[] = [];

  if (childElements && Array.isArray(childElements)) {
    // childElements are already in the correct format, just ensure they have proper values
    dndKitItems = childElements.map((child, index) => {
      const item = items?.[index];
      return {
        ...child,
        id: child.id || `item-${index}`,
        value: child.value || item,
      } as DragDropElementWrapper<T>;
    });
  } else if (items) {
    // If no childElements provided, create basic items
    dndKitItems = items.map((item, index) => ({
      id: `item-${index}`,
      element: item as any,
      dragAndDroppable: true,
      value: item,
    }));
  }

  return (
    <DnDKitDragDrop
      items={dndKitItems}
      tableHeadContent={tableHeadContent}
      onReorder={handleDndKitReorder}
      type={type}
      className={className}
      iconName={props.iconName}
      dense={dense}
      usePortal={props.usePortal}
    />
  );
};

export default DragDropContainer;
